import '@xterm/xterm/css/xterm.css';
import "../../static/css/terminal.css"

import {useNavigate, useParams} from "react-router-dom";
import WebSocketTerminal from "./WebSocketTerminal.tsx";
import TerminalStatusBar, {type TerminalStatusBarProps} from "./TerminalStatusBar.tsx";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import TerminalContainer, {type TerminalScreenContainerRef} from "./container/TerminalContainer.tsx";
import HelpDialog from "../terminal/HelpDialog.tsx";
import TerminalPasswordDialog, {type TerminalPasswordDialogRef} from "./TerminalPasswordDialog.tsx";
import {
    ContextMenuEventHandler,
    TerminalContainerEventHandlerImpl,
    TerminalPasswordDialogEventHandlerImpl,
    TerminalShortCutKeyHelperHandlerImpl,
    WebsocketTerminalEventHandlerImpl
} from "./TerminalEventHandler.ts";
import ContextMenu, {type ContextMenuRef} from "./ContextMenu.tsx";
import TerminalShortCutKeyHelper from "./TerminalShortCutKeyHelper.tsx";
import bell from "../../assets/bell.ogg";

function Terminal() {
    const instanceId = parseInt(useParams<{ instanceId: string }>().instanceId!, 10);
    const navigate = useNavigate();

    const terminalScreenContainerRef = useRef<TerminalScreenContainerRef>(null);
    const terminalPasswordDialogRef = useRef<TerminalPasswordDialogRef>(null);
    const [terminalStatusBarProps, setTerminalStatusBarProps] = useState<TerminalStatusBarProps>();
    const webSocketTerminal = useMemo<WebSocketTerminal>(
        () => {
            const eventHandler = new WebsocketTerminalEventHandlerImpl(
                navigate,
                instanceId,
                terminalScreenContainerRef,
                terminalPasswordDialogRef,
                setTerminalStatusBarProps
            );

            return new WebSocketTerminal(instanceId, eventHandler);
        }, [navigate, instanceId],
    )
    const terminalContainerEventHandler = useMemo(
        () => new TerminalContainerEventHandlerImpl(webSocketTerminal),
        [webSocketTerminal]
    );
    const terminalPasswordDialogEventHandler = useMemo(
        () => new TerminalPasswordDialogEventHandlerImpl(webSocketTerminal),
        [webSocketTerminal]
    );


    const terminalStatusBarRef = useRef<HTMLDivElement>(null);
    const contextMenuRef = useRef<ContextMenuRef>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    const [modalOpen, setModalOpen] = useState(false);

    const contextMenuEventHandler = new ContextMenuEventHandler(terminalScreenContainerRef);

    const terminalShortCutKeyHelper = useMemo(() => {
        return new TerminalShortCutKeyHelper(
            new TerminalShortCutKeyHelperHandlerImpl(setModalOpen, terminalScreenContainerRef)
        )
    }, [setModalOpen, terminalScreenContainerRef]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            terminalShortCutKeyHelper.keydown(event).finally();
        },
        [terminalShortCutKeyHelper]
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent) => {
            event.preventDefault();
            terminalShortCutKeyHelper.keyup(event,);
        },
        [terminalShortCutKeyHelper]
    );

    function onContextMenu(e: React.MouseEvent<HTMLElement, MouseEvent>) {
        e.preventDefault();
        contextMenuRef.current?.showModal(
            e.clientX, e.clientY,
        );
    }

    function onClickMain() {
        if (contextMenuRef.current?.dialogRef.open) {
            contextMenuRef.current?.dialogRef.close();
        }
    }


    // onClickCopy
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            // 페이지가 닫히는것 구분 하는 방법은?
            // webSocketTerminalRef.current?.close();

            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        }
    }, [handleKeyDown, handleKeyUp, instanceId, terminalStatusBarProps]);

    const belling = useRef(false);
    const onBellTerminal = useCallback(() => {
        const audioElem = audioRef.current;
        if (!audioElem) return;

        if (belling.current) {
            return;
        }

        belling.current = true;
        audioElem.pause();
        audioElem.currentTime = 0;
        audioElem.play().finally();

        setTimeout(() => {
            belling.current = false;
        }, 100);
    }, [belling]);


    return (
        <main className="single"
              onContextMenu={onContextMenu}
              onClick={onClickMain}
        >
            <audio ref={audioRef} src={bell} preload="auto"/>

            <div className="bg-light px-5 pb-3 min-vh-100">
                <div className="row" ref={terminalStatusBarRef}>
                    <TerminalStatusBar
                        instance_status={terminalStatusBarProps?.instance_status || ""}
                        console_status={terminalStatusBarProps?.console_status || ""}
                        instance_ip_list={terminalStatusBarProps?.instance_ip_list || []}
                        instance_name={terminalStatusBarProps?.instance_name || ""}
                        os_image={terminalStatusBarProps?.os_image || ""}
                        status_color={terminalStatusBarProps?.status_color}
                    />
                </div>

                <TerminalContainer
                    ref={terminalScreenContainerRef}
                    instanceId={instanceId}
                    onNewTab={(tab) => terminalContainerEventHandler.onNewTab(tab)}
                    onCloseTab={(tabKey) => terminalContainerEventHandler.onCloseTab(tabKey)}
                    onResize={(cols, rows) => terminalContainerEventHandler.onResize(cols, rows)}
                    onWriteStdIn={(tabKey, data) => terminalContainerEventHandler.onWriteStdIn(tabKey, data)}
                    statusBarSize={terminalStatusBarRef.current?.offsetHeight || 0}
                    osImage={terminalStatusBarProps?.os_image ?? ""}
                    onBell={onBellTerminal}
                />

                <HelpDialog open={modalOpen} onClose={() => setModalOpen(false)}/>
                <TerminalPasswordDialog
                    onEnterPassword={(password) => terminalPasswordDialogEventHandler.onEnterPassword(password)}
                    onCancel={() => terminalPasswordDialogEventHandler.onCancel()}
                    ref={terminalPasswordDialogRef}/>
                <ContextMenu
                    ref={contextMenuRef}
                    onClickCopy={() => contextMenuEventHandler.onClickCopy()}
                    onClickPaste={(content) => contextMenuEventHandler.onClickPaste(content)}

                    onClickNewTab={() => contextMenuEventHandler.onClickNewTab()}
                    onClickClose={() => contextMenuEventHandler.onClickClose()}
                />
            </div>
        </main>
    );
}

export default Terminal;